import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { MyContext } from "../../context/MyContext";

interface Product {
  productId: string;
  title: string;
  description: string;
  price: string;
  priceMicros: number;
  currencyCode: string;
  billingPeriod: string;
  trialPeriod: string | null;
  offerToken: string | null;
}

interface SubscriptionInfo {
  productId: string;
  purchaseToken: string;
  orderId: string;
  purchaseState: number;
  isAutoRenewing: boolean;
  isAcknowledged: boolean;
}

declare global {
  interface Window {
    Android?: {
      queryBillingProducts: () => void;
      launchBilling: (
        productId: string,
        offerToken: string | null,
        userId: string,
      ) => void;
      querySubscriptionStatus: () => void;
      openSubscriptionManagement: () => void;
      openPlayStorePaymentHistory: () => void;
      postMessage: (message: string) => void;
    };
    onBillingProducts?: (data: any) => void;
    onBillingResult?: (data: any) => void;
    onSubscriptionStatus?: (data: any) => void;
  }
}

const SubscriptionTest = () => {
  const { user, appVersion } = useContext(MyContext);
  const [isInApp, setIsInApp] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionInfo[]>([]);
  const [logs, setLogs] = useState<string[]>([]);
  const navigate = useNavigate();

  const userId = user?.id || "";

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs((prev) => [`[${timestamp}] ${message}`, ...prev]);
  };

  useEffect(() => {
    // 앱 환경 체크
    const checkInApp = typeof window.Android !== "undefined";
    setIsInApp(checkInApp);
    addLog(
      checkInApp ? "✅ 앱 환경 감지됨" : "❌ 웹 환경 (window.Android 없음)",
    );

    // 콜백 함수 등록
    window.onBillingProducts = (data) => {
      addLog("📦 상품 목록 콜백 수신");
      console.log("onBillingProducts:", data);
      if (data.ok) {
        setProducts(data.products || []);
        addLog(`✅ 상품 ${data.products?.length || 0}개 조회 성공`);
      } else {
        addLog(`❌ 상품 조회 실패: ${data.message}`);
      }
    };

    window.onBillingResult = (data) => {
      addLog("💳 결제 결과 콜백 수신");
      console.log("onBillingResult:", data);
      if (data.ok) {
        addLog(`✅ 결제 성공! 주문ID: ${data.orderId}`);
        addLog(`   - 상품: ${data.productId}`);
        addLog(`   - 토큰: ${data.purchaseToken}...`);

        // 서버 검증 API 호출
        const verifyPurchase = async () => {
          try {
            addLog("🔄 서버 검증 요청 중...");
            addLog(
              `   요청 URL: https://ncdev-dsapi.dongascience.com/api/popcorn/google-play/v1/billing/verify-purchase`,
            );
            addLog(
              `   요청 데이터: { purchaseToken: ${data.purchaseToken?.substring(
                0,
                20,
              )}..., productId: ${data.productId}, orderId: ${
                data.orderId
              }, userId: ${userId} }`,
            );

            const response = await fetch(
              "https://ncdev-dsapi.dongascience.com/api/popcorn/google-play/v1/billing/verify-purchase",
              {
                method: "POST",
                credentials: "include",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  purchaseToken: data.purchaseToken,
                  productId: data.productId,
                  orderId: data.orderId,
                  userId: userId,
                }),
              },
            );

            addLog(`   응답 상태: ${response.status} ${response.statusText}`);

            const result = await response.json();
            console.log("구매 검증 응답:", result);

            if (response.ok) {
              addLog("✅ 서버 검증 성공!");
              addLog(`   응답: ${JSON.stringify(result)}`);
            } else {
              addLog(
                `❌ 서버 검증 실패: ${response.status} ${response.statusText}`,
              );
              addLog(
                `   오류 메시지: ${result.message || JSON.stringify(result)}`,
              );
            }
          } catch (error) {
            console.error("검증 요청 실패:", error);
            addLog(`❌ 검증 요청 중 오류 발생`);

            if (error instanceof Error) {
              addLog(`   오류 메시지: ${error.message}`);
              addLog(`   오류 스택: ${error.stack}`);
            } else if (error instanceof TypeError) {
              addLog(`   오류 타입: TypeError (네트워크 오류)`);
              addLog(`   상세: ${String(error)}`);
            } else {
              addLog(`   오류: ${JSON.stringify(error)}`);
            }

            // CORS 오류 가능성
            if (
              error instanceof TypeError &&
              error.message.includes("Failed to fetch")
            ) {
              addLog(
                `   💡 가능한 원인: CORS 정책, 네트워크 연결, 서버 응답 없음, 또는 URL 오류`,
              );
              addLog(
                `   💡 확인 사항: 브라우저 console tab에서 더 자세한 오류 메시지 확인`,
              );
            }
          }
        };

        verifyPurchase();
      } else if (data.code === "USER_CANCELED") {
        addLog("⚠️ 사용자가 결제를 취소함");
      } else {
        addLog(`❌ 결제 실패: ${data.message} (코드: ${data.code})`);
      }
    };

    window.onSubscriptionStatus = (data) => {
      addLog("📊 구독 상태 콜백 수신");
      console.log("onSubscriptionStatus:", data);
      if (data.ok) {
        setSubscriptions(data.subscriptions || []);
        addLog(`✅ 활성 구독 ${data.subscriptions?.length || 0}개`);
      } else {
        addLog(`❌ 구독 상태 조회 실패: ${data.message}`);
      }
    };

    return () => {
      // cleanup
      delete window.onBillingProducts;
      delete window.onBillingResult;
      delete window.onSubscriptionStatus;
    };
  }, []);

  const handleQueryProducts = () => {
    if (!isInApp) {
      alert("인앱결제는 앱에서만 가능합니다.");
      return;
    }
    addLog("🔍 상품 목록 조회 요청...");
    window.Android?.queryBillingProducts();
  };

  const handleLaunchBilling = (product: Product) => {
    if (!isInApp) {
      alert("인앱결제는 앱에서만 가능합니다.");
      return;
    }
    addLog(`💳 결제 시작: ${product.title} (${product.productId})`);
    window.Android?.launchBilling(
      product.productId,
      product.offerToken,
      userId,
    );
  };

  const handleQuerySubscription = () => {
    if (!isInApp) {
      alert("인앱결제는 앱에서만 가능합니다.");
      return;
    }
    addLog("📊 구독 상태 조회 요청...");
    window.Android?.querySubscriptionStatus();
  };

  const handleOpenSubscriptionManagement = () => {
    if (!isInApp) {
      alert("이 기능은 앱에서만 사용 가능합니다.");
      return;
    }
    addLog("🔗 Google Play 구독관리 페이지로 이동...");
    window.Android?.openSubscriptionManagement();
  };

  const handleOpenPaymentHistory = () => {
    if (!isInApp) {
      alert("이 기능은 앱에서만 사용 가능합니다.");
      return;
    }
    addLog("🔗 Google Play 결제내역 페이지로 이동...");
    window.Android?.openPlayStorePaymentHistory();
  };

  return (
    <div className="w-full h-full overflow-auto bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* 헤더 */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-2xl font-bold mb-2">
                인앱결제 테스트 페이지
              </h1>
              <p className="text-gray-600">
                환경:{" "}
                <span
                  className={
                    isInApp
                      ? "text-green-600 font-bold"
                      : "text-red-600 font-bold"
                  }
                >
                  {isInApp ? "앱 (Android)" : "웹 브라우저"}
                </span>
              </p>
              <p className="text-sm text-gray-500 mt-1">사용자 ID: {userId}</p>
              {appVersion && (
                <p className="text-sm text-gray-500 mt-1">
                  앱 버전:{" "}
                  <span className="font-bold text-gray-700">{appVersion}</span>
                </p>
              )}
            </div>
            <button
              onClick={() => navigate("/subscription")}
              className="text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 px-3 py-2 rounded transition"
            >
              ← 구독 페이지로
            </button>
          </div>
        </div>

        {/* window.Android 함수 목록 */}
        {isInApp && (
          <div className="bg-blue-50 border-2 border-blue-300 p-6 rounded-lg shadow">
            <h2 className="text-xl font-bold mb-4">
              🔧 window.Android 사용 가능 함수
            </h2>
            <div className="bg-white p-4 rounded border border-gray-300">
              <div className="font-mono text-sm space-y-2">
                {Object.keys(window.Android || {})
                  .filter(
                    (key) =>
                      typeof (window.Android as any)?.[key] === "function",
                  )
                  .map((funcName) => (
                    <div
                      key={funcName}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      • {funcName}()
                    </div>
                  ))}
                {Object.keys(window.Android || {}).filter(
                  (key) => typeof (window.Android as any)?.[key] === "function",
                ).length === 0 && (
                  <p className="text-gray-500">사용 가능한 함수가 없습니다.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 기본 기능 테스트 */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-bold mb-4">📱 기본 기능</h2>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={handleQueryProducts}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-3 rounded-lg font-medium transition disabled:opacity-50"
              disabled={!isInApp}
            >
              📦 상품 목록 조회
            </button>
            <button
              onClick={handleQuerySubscription}
              className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-3 rounded-lg font-medium transition disabled:opacity-50"
              disabled={!isInApp}
            >
              📊 구독 상태 조회
            </button>
            <button
              onClick={handleOpenSubscriptionManagement}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-lg font-medium transition disabled:opacity-50"
              disabled={!isInApp}
            >
              ⚙️ 구독 관리
            </button>
            <button
              onClick={handleOpenPaymentHistory}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-3 rounded-lg font-medium transition disabled:opacity-50"
              disabled={!isInApp}
            >
              📜 결제 내역
            </button>
          </div>
        </div>

        {/* 상품 목록 */}
        {products.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-bold mb-4">💎 구독 상품</h2>
            <div className="space-y-3">
              {products.map((product) => (
                <div
                  key={product.productId}
                  className="border rounded-lg p-4 hover:border-blue-500 transition"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-bold text-lg">{product.title}</h3>
                      <p className="text-sm text-gray-600">
                        {product.description}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        ID: {product.productId}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-blue-600">
                        {product.price}
                      </p>
                      <p className="text-xs text-gray-500">
                        {product.billingPeriod}
                      </p>
                    </div>
                  </div>
                  {product.trialPeriod && (
                    <p className="text-sm text-green-600 mb-2">
                      🎁 무료체험: {product.trialPeriod}
                    </p>
                  )}
                  <button
                    onClick={() => handleLaunchBilling(product)}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium transition"
                  >
                    구독하기
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 현재 구독 상태 */}
        {subscriptions.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-bold mb-4">✅ 활성 구독</h2>
            <div className="space-y-3">
              {subscriptions.map((sub, idx) => (
                <div
                  key={idx}
                  className="border border-green-500 bg-green-50 rounded-lg p-4"
                >
                  <p className="font-bold">상품: {sub.productId}</p>
                  <p className="text-sm text-gray-600">
                    주문 ID: {sub.orderId}
                  </p>
                  <div className="flex gap-2 mt-2">
                    <span
                      className={`text-xs px-2 py-1 rounded ${
                        sub.isAutoRenewing
                          ? "bg-green-200 text-green-800"
                          : "bg-gray-200 text-gray-800"
                      }`}
                    >
                      {sub.isAutoRenewing ? "자동갱신 ON" : "자동갱신 OFF"}
                    </span>
                    <span
                      className={`text-xs px-2 py-1 rounded ${
                        sub.isAcknowledged
                          ? "bg-blue-200 text-blue-800"
                          : "bg-yellow-200 text-yellow-800"
                      }`}
                    >
                      {sub.isAcknowledged ? "승인됨" : "미승인"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 로그 */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">📝 로그</h2>
            <button
              onClick={() => setLogs([])}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              지우기
            </button>
          </div>
          <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm max-h-80 overflow-auto">
            {logs.length === 0 ? (
              <p className="text-gray-500">로그가 없습니다.</p>
            ) : (
              logs.map((log, idx) => (
                <div key={idx} className="mb-1">
                  {log}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionTest;
